import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, isStaff, homeFor } from "@/context/AuthContext";
import { Loader } from "@/components/common/Loader";
import { Splash } from "@/components/common/Splash";
import CenterLayout from "@/components/layout/CenterLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AccountStatus from "@/pages/auth/AccountStatus";
import CenterHome from "@/pages/center/Home";
import Documents from "@/pages/center/Documents";
import Tickets from "@/pages/center/Tickets";
import NewTicket from "@/pages/center/NewTicket";
import TicketDetail from "@/pages/center/TicketDetail";
import Account from "@/pages/center/Account";
import Social from "@/pages/center/Social";
import Orders from "@/pages/center/Orders";
import AdminSocial from "@/pages/admin/Social";
import AdminNews from "@/pages/admin/News";
import AdminEmails from "@/pages/admin/Emails";
import SharedDocuments from "@/pages/admin/SharedDocuments";
import Guide from "@/pages/center/Guide";
import Staff from "@/pages/admin/Staff";
import Dashboard from "@/pages/admin/Dashboard";
import Centers from "@/pages/admin/Centers";
import CenterDetail from "@/pages/admin/CenterDetail";
import AdminTickets from "@/pages/admin/Tickets";
import AdminTicketDetail from "@/pages/admin/TicketDetail";
import Settings from "@/pages/admin/Settings";
import AuditLog from "@/pages/admin/AuditLog";
import AdminAccount from "@/pages/admin/AdminAccount";

function PublicOnly() {
  const { user } = useAuth();
  const from = useLocation().state?.from;
  if (user === null) return <Loader full />;
  if (user) return <Navigate to={from && user.role === "center" ? from : homeFor(user)} replace />;
  return <Outlet />;
}

function CenterArea() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader full />;
  if (!user) return <Navigate to="/accedi" replace state={{ from: location.pathname }} />;
  if (isStaff(user)) return <Navigate to="/admin" replace />;
  if (user.center?.status !== "approved") return <AccountStatus />;
  return (
    <CenterLayout>
      <Outlet />
    </CenterLayout>
  );
}

function AdminArea({ superOnly }) {
  const { user } = useAuth();
  if (user === null) return <Loader full />;
  if (!user) return <Navigate to="/accedi" replace />;
  if (!isStaff(user)) return <Navigate to="/" replace />;
  if (superOnly && user.role !== "superadmin") return <Navigate to="/admin" replace />;
  return <Outlet />;
}

function AdminShell() {
  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicOnly />}>
            <Route path="/accedi" element={<Login />} />
            <Route path="/registrati" element={<Register />} />
            <Route path="/password-dimenticata" element={<ForgotPassword />} />
          </Route>
          <Route path="/reimposta-password" element={<ResetPassword />} />
          <Route element={<CenterArea />}>
            <Route path="/" element={<CenterHome />} />
            <Route path="/documenti" element={<Documents />} />
            <Route path="/assistenza" element={<Tickets />} />
            <Route path="/assistenza/nuovo" element={<NewTicket />} />
            <Route path="/assistenza/:id" element={<TicketDetail />} />
            <Route path="/account" element={<Account />} />
            <Route path="/guida" element={<Guide />} />
            <Route path="/contenuti-social" element={<Social />} />
            <Route path="/contenuti-social/:id" element={<Social />} />
            <Route path="/ordini" element={<Orders />} />
          </Route>
          <Route element={<AdminArea />}>
            <Route element={<AdminShell />}>
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin/centri" element={<Centers />} />
              <Route path="/admin/centri/:id" element={<CenterDetail />} />
              <Route path="/admin/ticket" element={<AdminTickets />} />
              <Route path="/admin/ticket/:id" element={<AdminTicketDetail />} />
              <Route path="/admin/account" element={<AdminAccount />} />
              <Route path="/admin/social" element={<AdminSocial />} />
              <Route path="/admin/novita" element={<AdminNews />} />
              <Route path="/admin/email" element={<AdminEmails />} />
              <Route path="/admin/documenti" element={<SharedDocuments />} />
              <Route element={<AdminArea superOnly />}>
                <Route path="/admin/impostazioni" element={<Settings />} />
                <Route path="/admin/registro" element={<AuditLog />} />
                <Route path="/admin/staff" element={<Staff />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
      <Splash />
    </AuthProvider>
  );
}
